import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth pages
import Login    from './pages/Login';
import Register from './pages/Register';

// Admin pages
import Overview     from './pages/Overview';
import Clients      from './pages/Clients';
import Campaigns    from './pages/Campaigns';
import Leads        from './pages/Leads';
import Invoices     from './pages/Invoices';
import AdCopy       from './pages/AdCopy';
import Competitors  from './pages/Competitors';
import Logs         from './pages/Logs';
import Users        from './pages/Users';

// Client pages
import ClientDashboard from './pages/client/ClientDashboard';
import ClientCampaigns from './pages/client/ClientCampaigns';
import ClientInvoices  from './pages/client/ClientInvoices';

// ── Styles ───────────────────────────────────────────
const S = {
  app:     { display: 'flex', minHeight: '100vh', fontFamily: 'Inter, sans-serif', background: '#0f0f17', color: '#e8e8f0' },
  sidebar: { width: 220, background: '#16161f', borderRight: '1px solid #1e1e2e', padding: '24px 0', flexShrink: 0, display: 'flex', flexDirection: 'column' },
  logo:    { padding: '0 24px 20px', fontSize: 15, fontWeight: 700, color: '#a29bfe', borderBottom: '1px solid #1e1e2e', marginBottom: 8 },
  navLink: { display: 'block', padding: '10px 24px', color: '#6b6b80', textDecoration: 'none', fontSize: 13 },
  active:  { color: '#a29bfe', background: 'rgba(108,99,255,0.1)', borderRight: '2px solid #6c63ff' },
  main:    { flex: 1, padding: 32, overflowY: 'auto' },
  userBar: { marginTop: 'auto', padding: '16px 24px', borderTop: '1px solid #1e1e2e' },
  userName:{ fontSize: 13, fontWeight: 600, color: '#e8e8f0', marginBottom: 4 },
  userRole:{ fontSize: 11, color: '#6b6b80', marginBottom: 10 },
  logoutBtn:{ background: 'none', border: '1px solid #1e1e2e', borderRadius: 8, padding: '7px 14px', color: '#6b6b80', cursor: 'pointer', fontSize: 12, width: '100%' },
  blocked: { minHeight: '100vh', background: '#0f0f17', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' },
};

const ADMIN_NAV = [
  { to: '/',            label: '📊 Overview' },
  { to: '/users',       label: '👤 Users' },
  { to: '/clients',     label: '👥 Clients' },
  { to: '/campaigns',   label: '📋 Campaigns' },
  { to: '/leads',       label: '🎯 Leads' },
  { to: '/invoices',    label: '🧾 Invoices' },
  { to: '/adcopy',      label: '✍️ Ad Copy' },
  { to: '/competitors', label: '🔍 Competitors' },
  { to: '/logs',        label: '🤖 AI Logs' },
];

const CLIENT_NAV = [
  { to: '/client',           label: '📊 Dashboard' },
  { to: '/client/campaigns', label: '📋 Campaigns' },
  { to: '/client/invoices',  label: '🧾 Invoices' },
];

// ── Layouts ───────────────────────────────────────────
function AdminLayout({ children }) {
  const { userProfile, logout } = useAuth();
  return (
    <div style={S.app}>
      <nav style={S.sidebar}>
        <div style={S.logo}>🤖 AI Ad Agency<br /><span style={{ fontSize: 10, color: '#ff6584', fontWeight: 400 }}>ADMIN</span></div>
        {ADMIN_NAV.map(n => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'}
            style={({ isActive }) => ({ ...S.navLink, ...(isActive ? S.active : {}) })}>
            {n.label}
          </NavLink>
        ))}
        <div style={S.userBar}>
          <div style={S.userName}>{userProfile?.name}</div>
          <div style={S.userRole}>Admin</div>
          <button style={S.logoutBtn} onClick={logout}>🚪 Logout</button>
        </div>
      </nav>
      <main style={S.main}>{children}</main>
    </div>
  );
}

function ClientLayout({ children }) {
  const { userProfile, logout } = useAuth();
  return (
    <div style={S.app}>
      <nav style={S.sidebar}>
        <div style={S.logo}>🤖 AI Ad Agency<br /><span style={{ fontSize: 10, color: '#55efc4', fontWeight: 400 }}>CLIENT PANEL</span></div>
        {CLIENT_NAV.map(n => (
          <NavLink key={n.to} to={n.to} end={n.to === '/client'}
            style={({ isActive }) => ({ ...S.navLink, ...(isActive ? S.active : {}) })}>
            {n.label}
          </NavLink>
        ))}
        <div style={S.userBar}>
          <div style={S.userName}>{userProfile?.name}</div>
          <div style={S.userRole}>{userProfile?.role} — {userProfile?.status}</div>
          <button style={S.logoutBtn} onClick={logout}>🚪 Logout</button>
        </div>
      </nav>
      <main style={S.main}>{children}</main>
    </div>
  );
}

// ── Route Guards ──────────────────────────────────────
function RequireAuth({ children, role }) {
  const { currentUser, userProfile, isBlocked } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (isBlocked) return (
    <div style={S.blocked}>
      <div style={{ textAlign: 'center', background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 20, padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⛔</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#ff6584', marginBottom: 8 }}>Account Blocked</div>
        <div style={{ fontSize: 14, color: '#6b6b80', marginBottom: 8 }}>আপনার account block করা হয়েছে।</div>
        {userProfile?.blockedReason && <div style={{ fontSize: 13, color: '#6b6b80' }}>কারণ: {userProfile.blockedReason}</div>}
        <div style={{ fontSize: 13, color: '#6b6b80', marginTop: 8 }}>Admin এর সাথে যোগাযোগ করুন।</div>
      </div>
    </div>
  );
  if (role && userProfile?.role !== role) {
    return <Navigate to={userProfile?.role === 'admin' ? '/' : '/client'} replace />;
  }
  return children;
}

// ── Main App ──────────────────────────────────────────
function AppRoutes() {
  const { currentUser, userProfile } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route path="/login"    element={!currentUser ? <Login /> : <Navigate to={userProfile?.role === 'admin' ? '/' : '/client'} />} />
      <Route path="/register" element={!currentUser ? <Register /> : <Navigate to={userProfile?.role === 'admin' ? '/' : '/client'} />} />

      {/* Admin routes */}
      <Route path="/" element={<RequireAuth role="admin"><AdminLayout><Overview /></AdminLayout></RequireAuth>} />
      <Route path="/users" element={<RequireAuth role="admin"><AdminLayout><Users /></AdminLayout></RequireAuth>} />
      <Route path="/clients" element={<RequireAuth role="admin"><AdminLayout><Clients /></AdminLayout></RequireAuth>} />
      <Route path="/campaigns" element={<RequireAuth role="admin"><AdminLayout><Campaigns /></AdminLayout></RequireAuth>} />
      <Route path="/leads" element={<RequireAuth role="admin"><AdminLayout><Leads /></AdminLayout></RequireAuth>} />
      <Route path="/invoices" element={<RequireAuth role="admin"><AdminLayout><Invoices /></AdminLayout></RequireAuth>} />
      <Route path="/adcopy" element={<RequireAuth role="admin"><AdminLayout><AdCopy /></AdminLayout></RequireAuth>} />
      <Route path="/competitors" element={<RequireAuth role="admin"><AdminLayout><Competitors /></AdminLayout></RequireAuth>} />
      <Route path="/logs" element={<RequireAuth role="admin"><AdminLayout><Logs /></AdminLayout></RequireAuth>} />

      {/* Client routes */}
      <Route path="/client" element={<RequireAuth role="client"><ClientLayout><ClientDashboard /></ClientLayout></RequireAuth>} />
      <Route path="/client/campaigns" element={<RequireAuth role="client"><ClientLayout><ClientCampaigns /></ClientLayout></RequireAuth>} />
      <Route path="/client/invoices" element={<RequireAuth role="client"><ClientLayout><ClientInvoices /></ClientLayout></RequireAuth>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
